
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { prepareImageUpload } from '@/utils/imageUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { ImagePreview } from '@/components/shared/ImagePreview';

interface TestResult {
  fileName: string;
  fileType: string;
  fileSize: string;
  uploadSuccess: boolean;
  displaySuccess: boolean;
  error?: string;
  imageUrl?: string;
}

const TEST_CASES = [
  { name: 'valid-small.jpg', type: 'image/jpeg', size: 50 * 1024 }, // 50KB
  { name: 'valid-medium.png', type: 'image/png', size: 500 * 1024 }, // 500KB
  { name: 'valid-large.jpg', type: 'image/jpeg', size: 2 * 1024 * 1024 }, // 2MB
  { name: 'invalid-type.txt', type: 'text/plain', size: 1024 }, // Invalid type
  { name: 'too-large.jpg', type: 'image/jpeg', size: 6 * 1024 * 1024 }, // 6MB
];

export const ImageValidationTest = () => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const createTestFile = (testCase: typeof TEST_CASES[0]): File => {
    const content = new ArrayBuffer(testCase.size);
    return new File([content], testCase.name, { type: testCase.type });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const runTests = async () => {
    setIsRunning(true);
    setResults([]);

    for (const testCase of TEST_CASES) {
      const file = createTestFile(testCase);
      const result: TestResult = {
        fileName: testCase.name,
        fileType: testCase.type,
        fileSize: formatFileSize(testCase.size),
        uploadSuccess: false,
        displaySuccess: false,
      };

      try {
        console.debug('[Test] Starting upload for:', testCase.name);
        // Pass both required arguments: file and bucketName
        const uploadResult = await prepareImageUpload(file, 'test-uploads');
        
        // Get the public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('test-uploads')
          .getPublicUrl(uploadResult.path);

        result.uploadSuccess = true;
        result.imageUrl = publicUrl;

        // Verify image display
        if (result.imageUrl) {
          const img = new Image();
          const loadPromise = new Promise((resolve, reject) => {
            img.onload = () => resolve(true);
            img.onerror = () => reject(new Error('Failed to load image'));
          });

          img.src = result.imageUrl;
          await loadPromise;
          result.displaySuccess = true;
        }
      } catch (error) {
        result.error = error instanceof Error ? error.message : 'Unknown error';
        console.error('[Test] Error for:', testCase.name, error);
      }

      setResults(prev => [...prev, result]);
    }

    setIsRunning(false);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Image Upload Validation Tests</h2>
        <Button 
          onClick={runTests} 
          disabled={isRunning}
        >
          {isRunning ? 'Running Tests...' : 'Run Tests'}
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>File Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Upload</TableHead>
            <TableHead>Display</TableHead>
            <TableHead>Preview</TableHead>
            <TableHead>Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((result, index) => (
            <TableRow key={index}>
              <TableCell>{result.fileName}</TableCell>
              <TableCell>{result.fileType}</TableCell>
              <TableCell>{result.fileSize}</TableCell>
              <TableCell>
                <span className={result.uploadSuccess ? 'text-green-500' : 'text-red-500'}>
                  {result.uploadSuccess ? '✓' : '✗'}
                </span>
              </TableCell>
              <TableCell>
                <span className={result.displaySuccess ? 'text-green-500' : 'text-red-500'}>
                  {result.displaySuccess ? '✓' : '✗'}
                </span>
              </TableCell>
              <TableCell>
                {result.imageUrl && (
                  <ImagePreview
                    src={result.imageUrl}
                    alt={result.fileName}
                    type="avatar"
                    className="w-10 h-10"
                  />
                )}
              </TableCell>
              <TableCell className="text-red-500 text-sm">
                {result.error}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
